/**
 * The single place this app talks to the controller.
 *
 * Everything goes through `apiFetch`, so bearer-token attachment, the error
 * convention and JSON parsing exist once. The controller's contract is that
 * failure is always a non-2xx carrying FastAPI's `{"detail": ...}` — there is
 * no 200-with-success=false to disambiguate, so a thrown `ApiError` is the
 * complete failure story and callers never inspect a body for a status.
 *
 * The token provider is registered by the auth layer rather than imported,
 * which keeps this module free of any dependency on React or on whether Auth0
 * is configured at all.
 */

import { apiBaseUrl } from './env';

/**
 * The double-submit half of CSRF protection.
 *
 * The session cookie is HttpOnly and unreadable here; this second, readable
 * cookie exists so the app can echo its value into a header. A cross-origin
 * form post cannot set a custom header, so a request carrying a matching one
 * demonstrably came from this app rather than from a page that merely knows the
 * browser holds a cookie for it.
 */
function readCsrfCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)fleet_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly path: string;

  constructor(status: number, detail: string, path: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.path = path;
  }

  /** We do not know who you are: no token, expired, or wrong audience. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** We know who you are, and you are not a platform operator. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Network failure, DNS, or the controller not running at all. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

/**
 * A 422's list of `{loc, msg, type}` as one readable sentence per field —
 * "user email: not a valid email address" — instead of raw JSON on screen.
 * Returns null for anything that is not that shape, so the caller falls back.
 */
function describeValidation(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const parts: string[] = [];
  for (const item of detail) {
    if (!item || typeof item !== 'object') return null;
    const { loc, msg, type } = item as { loc?: unknown; msg?: unknown; type?: unknown };
    if (typeof msg !== 'string') return null;
    const path = Array.isArray(loc)
      ? loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path')
      : [];
    const field = path.length > 0 ? String(path[path.length - 1]).replace(/_/g, ' ') : '';
    let text = msg;
    if (type === 'string_pattern_mismatch') {
      text = /email/i.test(field) ? 'not a valid email address' : 'cannot be blank or malformed';
    }
    parts.push(field ? `${field}: ${text}` : text);
  }
  return parts.join('; ');
}

/** Pull FastAPI's `detail` out of a response without assuming it is a string. */
async function readDetail(response: Response): Promise<string> {
  let raw: string;
  try {
    raw = await response.text();
  } catch {
    return `${response.status} ${response.statusText}`.trim();
  }
  if (!raw) return `${response.status} ${response.statusText}`.trim();

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
      const detail = (parsed as { detail: unknown }).detail;
      if (typeof detail === 'string' && detail) return detail;
      // FastAPI's validation errors put a list of {loc, msg, type} here.
      const readable = describeValidation(detail);
      if (readable) return readable;
      if (detail != null) return JSON.stringify(detail);
    }
  } catch {
    /* Not JSON — a proxy error page, say. Fall through to the raw text. */
  }
  return raw.slice(0, 500);
}

export interface ApiFetchOptions {
  signal?: AbortSignal;
  /** `/api/health` is unauthenticated; skip the token round-trip for it. */
  anonymous?: boolean;
  /** Defaults to GET. Use the verb helpers rather than setting this by hand. */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** JSON-serialised into the request body. */
  body?: unknown;
}

/**
 * Call `path` and parse the response as JSON, or throw `ApiError`.
 *
 * `path` is contract-relative (`/api/centers`); the base URL is prepended so a
 * split deployment needs no change at any call site.
 */
export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  // Only on state-changing methods, matching the server: it exempts GET, HEAD
  // and OPTIONS, and sending the header on a read would be noise rather than
  // protection.
  if (method !== 'GET') {
    const csrf = readCsrfCookie();
    if (csrf) headers['X-Fleet-CSRF'] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal: options.signal ?? null,
      // The session is a cookie now, so this is load-bearing rather than
      // incidental. Same-origin is correct for the normal deployment, where the
      // controller serves this bundle itself; a split-origin dev setup needs
      // 'include', which works because CORS here already sets
      // allow_credentials with an explicit origin list and never `*`.
      credentials: 'same-origin',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      `Could not reach the controller at ${apiBaseUrl || window.location.origin}${path}. ` +
        'It may not be running, or this browser may not be on the private network.',
      path,
    );
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readDetail(response), path);
  }

  if (response.status === 204) return undefined as T;

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(
      response.status,
      `${path} returned a ${response.status} that was not valid JSON.`,
      path,
    );
  }
}

export interface TextResponse {
  text: string;
  headers: Headers;
}

/**
 * GET `path` and return the body as text, with the response headers.
 *
 * For the one kind of response that is not JSON: a terminal recording, which
 * is asciicast text and whose completeness travels in a header
 * (`X-Recording-Complete`) because it is not part of the cast format. Errors
 * follow the same `ApiError` convention as `apiFetch`, so a pruned (410) or
 * tampered (409) recording reads its `detail` exactly like any other failure.
 */
export async function apiFetchText(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<TextResponse> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: 'GET',
      headers: { Accept: 'text/plain, application/json' },
      signal: options.signal ?? null,
      credentials: 'same-origin',
      // A recording may hold patient data: never serve it from, or leave it
      // in, the HTTP cache. The server sends no-store too; this is the
      // browser half of the same rule.
      cache: 'no-store',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      `Could not reach the controller at ${apiBaseUrl || window.location.origin}${path}. ` +
        'It may not be running, or this browser may not be on the private network.',
      path,
    );
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readDetail(response), path);
  }
  return { text: await response.text(), headers: response.headers };
}

/**
 * POST `path` with a JSON body.
 *
 * Separate from `apiFetch` so that every mutating call in this app is
 * grep-able: there are three of them, they are the only way the panel changes
 * anything, and they should stay easy to enumerate.
 */
export async function apiPost<T>(
  path: string,
  body?: unknown,
  options: Omit<ApiFetchOptions, 'method' | 'body'> = {},
): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'POST', body: body ?? {} });
}

/** PUT `path` with a JSON body — the edit half of managing a resource. */
export async function apiPut<T>(
  path: string,
  body?: unknown,
  options: Omit<ApiFetchOptions, 'method' | 'body'> = {},
): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'PUT', body: body ?? {} });
}

/** DELETE `path`. A body is allowed (some deletes carry a confirmation). */
export async function apiDelete<T>(
  path: string,
  body?: unknown,
  options: Omit<ApiFetchOptions, 'method' | 'body'> = {},
): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'DELETE', body });
}

/**
 * Absolute WebSocket URL for a contract-relative path.
 *
 * The terminal ticket already carries its own `?ticket=…`, so `path` is used
 * verbatim. When `apiBaseUrl` is set (a split deployment) its scheme is swapped
 * for ws/wss; otherwise the socket is same-origin, matching how the bundle is
 * served.
 */
export function websocketUrl(path: string): string {
  if (apiBaseUrl) {
    return apiBaseUrl.replace(/^http/, 'ws') + path;
  }
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${path}`;
}

/** Human-readable one-liner for any thrown value, for error states. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return String(error);
}
